<template>
  <div
    class="qtl-note"
    :class="{ 'qtl-note--editable': canShowCommentEditButton, 'qtl-note--menu-open': menuOpen }"
  >
    <native-input
      ref="inputRef"
      class="qtl-note__input"
      :break-anywhere="true"
      :break-letters="isTableComment"
      :inline-icons="true"
      :show-border="false"
      :model-value="textContentInner"
      :edit-on-click="isCommentEditing"
      :submit-on-enter="true"
      :revert-on-escape="true"
      :disabled-field="!canEditContent || !isCommentEditing"
      :clear-hotkey="canEditContent && isCommentEditing"
      @update:caretPosition="onCaretPositionChange"
      @update:modelValue="onCommentInput"
      @click="onNativeInputClick"
      @commit="onCommentCommit"
      @cancel="onCommentCancel"
      @blur="onCommentBlur"
    />

    <button
      v-if="isCommentEditing"
      ref="templateBtnRef"
      type="button"
      class="qtl-note__btn qtl-note__btn--template"
      :aria-label="t(templatesTooltipKey)"
      @mousedown.prevent
      @click.stop="openTemplateMenu"
    >
      <span class="qtl-note__ico"></span>
      <q-tooltip
        :delay="300"
        anchor="top middle"
        self="center middle"
        class="qtl-sort-tooltip"
      >
        {{ t(templatesTooltipKey) }}
      </q-tooltip>
    </button>

    <button
      v-else-if="canShowCommentEditButton"
      type="button"
      class="qtl-note__btn qtl-note__btn--edit"
      :aria-label="t('edit_label')"
      @mousedown.prevent
      @click.stop="startCommentEditing()"
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14.1477 4.41553C14.3628 4.20033 14.3168 3.80543 14.0449 3.53355L12.4648 1.95342C12.1928 1.68151 11.7979 1.63539 11.5827 1.85061L10.2832 3.15015L12.8481 5.71509L14.1477 4.41553Z" fill="currentColor"/>
        <path d="M1.62408 14.5124C2.18081 14.3905 3.8676 13.9498 4.45828 13.7947C4.59405 13.7592 4.71797 13.688 4.81712 13.5885L11.8834 6.5228L9.47552 4.11511L2.40924 11.1808C2.30977 11.28 2.23865 11.4039 2.20299 11.5397C2.04786 12.1305 1.60733 13.8176 1.48615 14.3748C1.47758 14.4133 1.48933 14.4535 1.51726 14.4813C1.5452 14.5093 1.58556 14.5209 1.62408 14.5124Z" fill="currentColor"/>
      </svg>
      <q-tooltip
        :delay="300"
        anchor="top middle"
        self="center middle"
        class="qtl-sort-tooltip"
      >
        {{ t("edit_label") }}
      </q-tooltip>
    </button>

    <mosta-context-menu
      :visible="menuOpen"
      :position="menuPos"
      :close-on-esc="true"
      @close="closeTemplateMenu"
    >
      <div class="qtl-note__template-menu">
        <aux-selector
          :model-type="
            isTableComment
              ? 33
              : genstore.selectedItem?.DestType === 7
              ? 24
              : 25
          "
          :preferred-segment="genstore.selectedItem?.DestType"
          :input-index="0"
          :allow-multiple="false"
          :allow-free-input="true"
          v-model="comment_templates"
          @deleteItem="deleteCommentTemplate"
          @update:caretPosition="onCaretPositionChange"
          @input-value="(val) => addCommentTemplate(val)"
          @selectorClosed="closeTemplateMenu"
        />
      </div>
    </mosta-context-menu>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { i18n } from "@/i18n";
import {
  AnalyticsV2ChartType,
  AnalyticsV2ElementType,
  ColumnCategory,
} from "@/models/etc/types";
import { BaseItemTypes } from "@/components/dnd/ItemTypes";
import { getIdentityKind } from "@/components/common/base-list/interfaces/ListTypes";
import type {
  CellDataSimple,
  TableDataCell,
} from "@/models/interfaces/entity-settings/querytable/querytable";
import type {
  AnalyticsChartIdentity,
  AnalyticsElementIdentity,
} from "@/models/interfaces/entity-settings/charts/charts";
import type { AnalyticsCommentAddOrUpdateRequest } from "@/models/interfaces/analytics/analytics-comments";
import { generalStore } from "@/store/general";
import { analyticsCommentsStore } from "@/store/analytics-comments";
import { storagePositionsStore } from "@/store/storage/storage-positions";
import { storageMovementsStore } from "@/store/storage/storage-movements";
import type { StorageDetailsTemplateDto } from "@/models/interfaces/storage/storage-models";
import {
  clearCommentEditRequest,
  consumeCommentEditingHandoff,
  isCommentEditRequested,
  markCommentEditingEnded,
} from "../commentColumn";
import { canEditStorageDetails } from "../notesAccess";
import MostaContextMenu from "@/components/common/basic/MostaContextMenu.vue";
import NativeInput from "@/components/common/basic/NativeInput.vue";

const props = defineProps<{
  element: TableDataCell;
  entityId?: number;
  creatorId?: number;
}>();

const t = (k: string) => i18n.global.t(k);
const genstore = generalStore();
const analyticsComments = analyticsCommentsStore();
const storagePos = storagePositionsStore();
const storageMove = storageMovementsStore();

const inputRef = ref<any>(null);
const templateBtnRef = ref<HTMLElement | null>(null);

const isCommentEditing = ref(false);
const textContentInner = ref("");
const originalComment = ref("");
// Должен быть ref, а не reactive: aux-selector отдаёт выбор через переназначение
// v-model (update:modelValue с новым массивом). reactive-const переназначить нельзя,
// поэтому watcher не срабатывал бы и вставка шаблона не работала.
const comment_templates = ref<StorageDetailsTemplateDto[]>([]);
const lastCommentCaretPosition = ref(0);
let _blockCaretUpdate = false;
// Клик, которым закрыли правку, не должен открывать её заново.
let skipNextCellClick = false;
const tids = ref<number[]>([]);

const menuOpen = ref(false);
const menuPos = ref<{ top: string; left: string }>({ top: "0px", left: "0px" });

// Пустые строки в начале и в конце комментария не показываем и не сохраняем,
// внутренние переносы и их количество остаются как ввёл пользователь.
function trimEdgeBlankLines(value: string): string {
  return (value || "")
    .replace(/^(?:[ \t]*\r?\n)+/, "")
    .replace(/(?:\r?\n[ \t]*)+$/, "");
}

// Значение комментария собираем из Data (строковые значения, переносы как есть).
// Пустым считаем только null/undefined; 0 не превращаем в пустую строку.
const textContent = computed(() =>
  trimEdgeBlankLines(
    props.element.Data.map((cell) => {
      const v = (cell as CellDataSimple).Value;
      return v === null || v === undefined ? "" : String(v);
    }).join(" "),
  ),
);

textContentInner.value = textContent.value;
updateLastCaretPosition();

// Строки таблицы переиспользуются при обновлении данных (например после массовой
// правки), поэтому локальный текст пересинхронизируем с пришедшей ячейкой. Пока идёт
// правка, значение принадлежит пользователю и не перетирается.
watch(textContent, (val) => {
  if (isCommentEditing.value) return;
  textContentInner.value = val;
  updateLastCaretPosition();
});

// Категория колонки решает, куда сохранять и какие шаблоны показывать.
const isTableComment = computed(
  () => props.element.Category === ColumnCategory.TableComments,
);

const templatesTooltipKey = computed(() =>
  isTableComment.value ? "comment_templates" : "info_templates",
);

// const isMovementNote = computed(
//   () =>
//     genstore?.selectedItem?.DestType ===
//     AnalyticsV2ElementType.StorageItemMovement,
// );

const storageWA = computed(() => canEditStorageDetails());

// Изначально был запрет менять комментарий не его создателю, в ТЗ по комментариям было сказано убрать запрет
// const canEditMovementCommentByAuthor = computed(() => {
//   if (!isMovementNote.value) return true;
//   const creatorId = Number(props.creatorId ?? 0);
//   const changerId = Number(rootstore?.oldAPIUID ?? 0);
//   return creatorId > 0 && changerId > 0 && creatorId === changerId;
// });

const canEditTableComment = computed(
  () => !!genstore?.accessRights?.GeneralAccess,
);

const canShowCommentEditButton = computed(() =>
  isTableComment.value ? canEditTableComment.value : storageWA.value,
);

const canEditContent = computed(() =>
  isTableComment.value ? canEditTableComment.value : storageWA.value,
);

function onCaretPositionChange(val: number) {
  if (val >= 0 && !_blockCaretUpdate) {
    lastCommentCaretPosition.value = +val;
  }
}

// Вставка текста выбранного шаблона в позицию каретки.
watch(comment_templates, () => {
  if (!comment_templates.value.length) return;

  const templateText = (comment_templates.value[0] as StorageDetailsTemplateDto)
    .Text;
  if (!isTableComment.value) {
    tids.value.push(
      (comment_templates.value[0] as StorageDetailsTemplateDto).Id,
    );
  }

  const original = textContentInner.value || "";

  let c_pos = lastCommentCaretPosition.value;
  if (c_pos < 0) c_pos = 0;
  else if (c_pos > original.length) c_pos = original.length;

  _blockCaretUpdate = true;
  textContentInner.value =
    original.slice(0, c_pos) + templateText + original.slice(c_pos);
  lastCommentCaretPosition.value = c_pos + templateText.length;
  comment_templates.value = [];

  menuOpen.value = false;
  nextTick(() => {
    _blockCaretUpdate = false;
    returnFocusToEditor(lastCommentCaretPosition.value);
  });
});

// Смена modelValue при потерянном фокусе гасит режим правки внутри NativeInput, и его
// вотчер отрабатывает в своём nextTick. Поэтому фокус и каретку возвращаем таймаутом,
// уже после того как поле применило новое значение.
function returnFocusToEditor(caretPos: number) {
  if (!isCommentEditing.value) return;
  setTimeout(() => {
    if (!isCommentEditing.value || !inputRef.value?.startEdit) return;
    inputRef.value.startEdit();
    nextTick(() => setCaretAt(caretPos));
  }, 0);
}

// startEdit выделяет всё содержимое, поэтому после него ставим каретку в нужное
// место, иначе первый же введённый символ затрёт весь комментарий.
function setCaretAt(caretPos: number) {
  const field = inputRef.value?.$refs?.editable_field as HTMLElement | null;
  if (!field) return;
  const node = field.firstChild;
  const sel = window.getSelection();
  const range = document.createRange();
  if (node && node.nodeType === Node.TEXT_NODE) {
    const len = (node.textContent || "").length;
    range.setStart(node, Math.max(0, Math.min(caretPos, len)));
  } else {
    range.selectNodeContents(field);
  }
  range.collapse(true);
  sel?.removeAllRanges();
  sel?.addRange(range);
  lastCommentCaretPosition.value = caretPos;
}

// Узел и смещение под точкой экрана. Стандартный способ это caretPositionFromPoint, а
// caretRangeFromPoint это старый нестандартный вариант, который до сих пор есть в Blink и
// WebKit, поэтому спрашиваем оба.
function caretPointAt(x: number, y: number): { node: Node; offset: number } | null {
  const positionFromPoint = (document as any).caretPositionFromPoint;
  if (typeof positionFromPoint === "function") {
    const pos = positionFromPoint.call(document, x, y);
    if (pos?.offsetNode) return { node: pos.offsetNode, offset: pos.offset };
  }
  const rangeFromPoint = (document as any).caretRangeFromPoint;
  if (typeof rangeFromPoint === "function") {
    const range: Range | null = rangeFromPoint.call(document, x, y);
    if (range) return { node: range.startContainer, offset: range.startOffset };
  }
  return null;
}

// Позиция каретки по точке клика, чтобы правка начиналась там, куда ткнул пользователь.
// Точка вне текста (например пустое место ячейки) даёт конец текста.
function caretOffsetFromPoint(ev: MouseEvent): number {
  const field = inputRef.value?.$refs?.editable_field as HTMLElement | null;
  const point = field ? caretPointAt(ev.clientX, ev.clientY) : null;
  if (!field || !point || !field.contains(point.node)) {
    return textContentInner.value.length;
  }
  const pre = document.createRange();
  pre.selectNodeContents(field);
  pre.setEnd(point.node, point.offset);
  return pre.toString().length;
}

function updateLastCaretPosition() {
  if (lastCommentCaretPosition.value < textContentInner.value.length) {
    lastCommentCaretPosition.value = textContentInner.value.length;
  }
}

function addCommentTemplate(t_name: string) {
  if (
    t_name &&
    t_name.length &&
    textContentInner.value &&
    textContentInner.value.length
  ) {
    if (isTableComment.value) {
      analyticsComments.createCommentTemplate({
        Name: t_name,
        Text: textContentInner.value,
        Segment: genstore.selectedItem?.DestType,
      });
    } else if (
      genstore.selectedItem?.DestType === AnalyticsV2ElementType.StorageItem
    ) {
      storagePos.createNoteTemplate({
        Name: t_name,
        Text: textContentInner.value,
      });
    } else if (
      genstore.selectedItem?.DestType ===
      AnalyticsV2ElementType.StorageItemMovement
    ) {
      storageMove.createMovementNoteTemplate({
        Name: t_name,
        Text: textContentInner.value,
      });
    }
  }
}

function deleteCommentTemplate() {}

function onCommentInput(newname: string) {
  textContentInner.value = newname;
}

// commit приходит до blur и несёт итоговый текст поля. Сохранение вешаем не на него, а
// на blur: текст мог измениться и без ввода с клавиатуры, например вставкой шаблона.
function onCommentCommit(value: string) {
  if (!isCommentEditing.value || menuOpen.value) return;
  textContentInner.value = value;
}

function onCommentCancel() {
  if (!isCommentEditing.value || menuOpen.value) return;
  clearCommentEditRequest();
  cancelCommentEditing();
}

// Нажатие по свободному месту ячейки во время правки завершает её: contenteditable
// теряет фокус уже на mousedown, поэтому здесь только гасим последующий click, иначе он
// тут же открыл бы правку заново.
function onCellMouseDown(ev: MouseEvent) {
  if (!isCommentEditing.value) return;

  const target = ev.target as HTMLElement | null;
  const field = inputRef.value?.$refs?.editable_field as HTMLElement | null;
  if (!field || !target || field.contains(target)) return;
  if (target.closest(".qtl-note__btn, .native-input__clear-wrap, .native-input__error-wrap")) {
    return;
  }

  skipNextCellClick = true;
  field.blur();
}

function onCellClick(ev: MouseEvent) {
  if (skipNextCellClick) {
    skipNextCellClick = false;
    return;
  }
  if (isCommentEditing.value) return;
  // Этим кликом пользователь закрыл правку в другой строке, открывать её здесь не нужно.
  if (consumeCommentEditingHandoff()) return;

  startCommentEditing(caretOffsetFromPoint(ev));
}

// Нажимать и кликать можно по всей площади ячейки, а не только по этому блоку, поэтому
// mousedown и click приходят от того, кто рисует ячейку. Оба обработчика решают только
// по цели события, и цель в виде самой ячейки для них такая же, как нажатие мимо поля.
defineExpose({
  handleCellMouseDown: onCellMouseDown,
  handleCellClick: onCellClick,
});

// Уход фокуса завершает правку: Enter и клик мимо приходят сюда одинаково. Отмену по Esc
// это не задевает, там cancel уже закрыл режим и вернул исходный текст.
function onCommentBlur() {
  if (!isCommentEditing.value || menuOpen.value) return;
  clearCommentEditRequest();
  isCommentEditing.value = false;
  // Правку закрыл клик за пределами этой ячейки: гасим правку в той ячейке, куда он попал.
  // Клик по свободному месту своей же ячейки сюда не относится, его ловит skipNextCellClick.
  if (!skipNextCellClick) markCommentEditingEnded();
  saveComment();
  _blockCaretUpdate = true;
  setTimeout(() => {
    _blockCaretUpdate = false;
  }, 0);
}

function onNativeInputClick() {
  if (!isCommentEditing.value) return;
  setTimeout(() => {
    const field = inputRef.value?.$refs?.editable_field as HTMLElement | null;
    if (!field) return;
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      const pre = range.cloneRange();
      pre.selectNodeContents(field);
      pre.setEnd(range.endContainer, range.endOffset);
      lastCommentCaretPosition.value = pre.toString().length;
    }
  }, 0);
}

function openTemplateMenu(ev: MouseEvent) {
  const rect = templateBtnRef.value?.getBoundingClientRect();
  if (rect) {
    menuPos.value = { top: `${rect.top}px`, left: `${rect.left}px` };
  } else {
    menuPos.value = { top: `${ev.clientY}px`, left: `${ev.clientX}px` };
  }
  menuOpen.value = true;
  // Позицию уточняем после рендера, когда известна реальная ширина меню: открываем
  // слева от кнопки, чтобы меню её не перекрывало, и держим в пределах экрана.
  // nextTick-правка проходит до отрисовки браузером, поэтому мигания нет.
  nextTick(() => adjustTemplateMenuPosition());
}

// Закрытие меню без выбора шаблона тоже возвращает фокус в поле, иначе ячейка
// осталась бы в режиме правки без фокуса и без сохранения по клику мимо.
function closeTemplateMenu() {
  if (!menuOpen.value) return;
  menuOpen.value = false;
  returnFocusToEditor(lastCommentCaretPosition.value);
}

function adjustTemplateMenuPosition() {
  const MARGIN = 8;
  const inner = document.querySelector(".qtl-note__template-menu");
  const menuEl = inner?.closest(".mosta-context-menu") as HTMLElement | null;
  const rect = templateBtnRef.value?.getBoundingClientRect();
  if (!menuEl) return;

  const menuW = menuEl.offsetWidth || 200;
  const menuH = menuEl.offsetHeight || 0;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // По умолчанию меню уходит влево от кнопки, чтобы не накрывать её собой.
  let left = rect ? rect.left - menuW - 4 : parseFloat(menuPos.value.left) || 0;
  let top = rect ? rect.top : parseFloat(menuPos.value.top) || 0;

  // Слева не помещается - разворачиваем меню вправо от кнопки.
  if (rect && left < MARGIN) {
    left = rect.right + 4;
  }

  // Финальный клэмп по обоим краям экрана.
  left = Math.max(MARGIN, Math.min(left, vw - menuW - MARGIN));
  top = Math.max(MARGIN, Math.min(top, vh - menuH - MARGIN));

  menuPos.value = { top: `${top}px`, left: `${left}px` };
}

// Колонку только что включили кнопкой в строке: открываем правку в этой ячейке. Следим
// именно за заявкой, а не за монтированием: строка может перемонтироваться, пока таблица
// доводит ширины колонок, и тогда правку откроет уже новая ячейка.
watch(
  () => isCommentEditRequested(props.entityId),
  (requested) => {
    if (!requested || !isTableComment.value || !canShowCommentEditButton.value) return;
    if (isCommentEditing.value) return;
    startCommentEditing();
  },
  { immediate: true },
);

function startCommentEditing(caretPos?: number) {
  if (!canShowCommentEditButton.value) return;
  originalComment.value = textContentInner.value;
  isCommentEditing.value = true;
  const pos = caretPos ?? textContentInner.value.length;
  nextTick(() => {
    if (!inputRef.value?.startEdit) return;
    inputRef.value.startEdit();
    nextTick(() => setCaretAt(pos));
  });
}

function cancelCommentEditing() {
  textContentInner.value = originalComment.value;
  isCommentEditing.value = false;
  tids.value.splice(0);
}

// TODO: правка идёт прямо в из @Prop. Когда в появится
// метод обновления ячейки по EntityId и категории, перевести на него и убрать disable.
function applySavedValue(value: string) {
  textContentInner.value = value;
  const first = props.element.Data[0] as CellDataSimple | undefined;
  if (!first) return;
  first.Value = value;
  // eslint-disable-next-line vue/no-mutating-props
  if (props.element.Data.length > 1) props.element.Data.splice(1);
}

function commentTarget(): Partial<AnalyticsCommentAddOrUpdateRequest> {
  const el = genstore.selectedItem;
  if (!el) return {};

  if (
    el.elType === BaseItemTypes.BAR_CHART ||
    el.elType === BaseItemTypes.PIE_CHART ||
    el.elType === BaseItemTypes.GRAPH
  ) {
    return {
      ChartIdentity: {
        ChartId: el.Id,
        ChartType:
          el.elType === 2
            ? AnalyticsV2ChartType.Linear
            : el.elType === 3
            ? AnalyticsV2ChartType.Pie
            : AnalyticsV2ChartType.Bar,
        Destination: el.DestType,
      } as AnalyticsChartIdentity,
    };
  }

  return {
    ElementIdentity: {
      ElementId: el.Id,
      Source: [0, 1, 2, 3, 4, 5, 6, 7, 8].includes(el.SrcType)
        ? el.SrcType
        : null,
      Destination: el.DestType,
      Kind: getIdentityKind(el),
    } as AnalyticsElementIdentity,
  };
}

async function saveComment() {
  const newText = trimEdgeBlankLines(textContentInner.value);
  if (newText === originalComment.value) {
    textContentInner.value = newText;
    tids.value.splice(0);
    return;
  }

  if (isTableComment.value) {
    const res = await analyticsComments.addOrUpdateComment({
      EntityId: props.entityId,
      NoteValue: newText,
      ...commentTarget(),
    });
    applySavedValue(res?.data?.Value || newText);
  } else if (
    genstore.selectedItem?.DestType === AnalyticsV2ElementType.StorageItem
  ) {
    const res = await storagePos.editStorageItemDetails({
      ElementId: props.entityId,
      Value: newText,
      UsedDetailsTemplates: tids.value,
    });
    applySavedValue(res?.data?.Value || newText);
  } else if (
    genstore.selectedItem?.DestType ===
    AnalyticsV2ElementType.StorageItemMovement
  ) {
    const res = await storageMove.editMovementDetails({
      ElementId: props.entityId,
      Value: newText,
      UsedDetailsTemplates: tids.value,
    });
    applySavedValue(res?.data?.Value || newText);
  }
  tids.value.splice(0);
  isCommentEditing.value = false;
}
</script>

<style lang="scss" scoped>
.qtl-note {
  display: flex;
  align-items: baseline;
  flex-wrap: nowrap;
  gap: 2px;
  width: 100%;
}

.qtl-note--editable {
  cursor: text;
}

.qtl-note__input {
  width: -moz-available;
  width: -webkit-fill-available;
  margin: 0;
}

.qtl-note :deep(.native-input) {
  padding: 3px 7px;
  min-height: auto;
}

.qtl-note :deep(.native-input),
.qtl-note :deep(.native-input:focus-within),
.qtl-note :deep(.native-input.is-editable:hover) {
  border-color: transparent !important;
}

.qtl-note :deep(.inner-editable) {
  color: #4D4D4D;
  font-family: Roboto;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
}

.qtl-note :deep(.native-input:focus-within .inner-editable) {
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: auto;
  text-decoration-color: #3F884F;
  text-decoration-thickness: 10%;
  text-underline-offset: auto;
  text-underline-position: from-font;
}

.qtl-note__btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 4px 8px 0 0;
  line-height: 0;
  border-radius: 3px;
  align-self: flex-start;
  color: #979797;  visibility: hidden;
  opacity: 0;
  transition: opacity 0.2s;
}

.qtl-note:hover .qtl-note__btn {
  visibility: visible;
  opacity: 1;
}

.qtl-note--menu-open .qtl-note__btn {
  visibility: visible;
  opacity: 1;
}

.qtl-note--menu-open .qtl-note__btn--template {
  color: #3F884F;
}

.qtl-note:hover .qtl-note__btn--edit {
  color: #3F884F;
}

.qtl-note__btn--template:hover {
  color: #3F884F;
}

.qtl-note__ico {
  width: 16px;
  height: 16px;
  display: block;
  background-color: currentColor;
  mask: url("@/assets/icon-comment.svg") no-repeat center / contain;
  -webkit-mask: url("@/assets/icon-comment.svg") no-repeat center / contain;
}

.qtl-note__btn svg {
  display: block;
}

.qtl-note__template-menu {
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}
</style>
